'use client';

import {
  FC,
  MouseEventHandler,
  ReactNode,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { DocumentsCellProps } from './DocumentsCell.types';
import {
  Checkbox,
  CellCheckbox,
  CellContainer,
  Flex,
  Dialog,
  Dropdown,
  Text,
  Icon,
  Timestamp,
  type ResultListItem,
  ButtonGroup,
  Button,
  Pill,
  RestoreDocumentDialog,
  SecureDocumentShareDialog,
} from '@/_components';
import { IconType } from '@/_types/icons';
import { DirectoryType, DocumentItem } from '@/_types/models/documents';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import {
  getFileItemPath,
  getDocumentIcon,
  getIsClientActionsFolderDisabled,
} from '@/_utils/documents';
import { saveAs } from '@/_utils/files';
import { getIsPastDay } from '@/_utils/dates';
import {
  convertToPdfsAndDownloadFolder,
  downloadDocument,
  downloadFolder,
} from '@/_api/client-documents';
import { checkExistingSecureShare } from '@/_api/secure-document-shares';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getClasses } from '@/_utils/classnames';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const DocumentsCell: FC<DocumentsCellProps> = ({
  isFolder,
  onFileDelete,
  onFileRename,
  onItemCopy,
  onItemMove,
  type,
  onEditDescription,
  isPreview,
  onEditTags,
  onAttachToQuestionnaire,
  className,
  document,
  isLastColumn,
  isSelected,
  shouldDisableClientActions: shouldDisableClientActionsFromProps,
  toggleSelect,
}) => {
  const {
    companyId,
    openDirectory,
    selectedDocuments,
    onToggleDirectory,
    onCopyFileLocation,
  } = useContext(DocumentsContext);
  const { permissions, token, isUserReadOnly, isCompanyReadOnly, isAdvisor } =
    useContext(UserContext);
  const {
    setPreviewFile,
    setPreviewFileOriginalLink,
    setPreviewCompanyIdForAnnotations,
    setPreviewExtractedDocumentId,
    setPreviewDocumentCreatedAt,
    setPreviewDocumentStatus,
  } = useContext(PreviewContext);
  const { preferences } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);
  const { selectedCompanyQuestionnaires } = useContext(CompanyContext);
  const [isDownloadFolderDialogOpen, setIsDownloadFolderDialogOpen] =
    useState(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [isSecureShareDialogOpen, setIsSecureShareDialogOpen] = useState(false);
  const [hasExistingSecureShare, setHasExistingSecureShare] = useState(false);

  const icon: IconType = getDocumentIcon(document);

  const isNew: boolean =
    document?.type === 'file' && getIsPastDay(document?.created_at);
  const isReadOnly: boolean = isUserReadOnly || isCompanyReadOnly;
  const isAttached: boolean = !!document?.questionnaire_id;
  const isDeleted: boolean = !!document?.deleted_at;

  const attachedQuestionnaire: string = useMemo(() => {
    const questionnaire = selectedCompanyQuestionnaires.find(
      (questionnaire) => questionnaire.id === document.questionnaire_id,
    );

    return questionnaire
      ? `${questionnaire.formatted_name} (${questionnaire.tax_year})`
      : '';
  }, [document.questionnaire_id, selectedCompanyQuestionnaires]);

  const onClickFolder: MouseEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const item: DocumentItem | undefined = selectedDocuments?.find(
      (d) => d.link === document?.link,
    );

    if (!item) return;

    const path = getFileItemPath(item?.link);
    return onToggleDirectory(item as DirectoryType, path);
  };

  // https://github.com/Soraban/soraban-react-app/pull/690
  const getIsFolderOfClientActionsFolderDisabled = () => {
    if (shouldDisableClientActionsFromProps) return true;
    if (document?.type !== 'folder') return false;

    return getIsClientActionsFolderDisabled(
      isAdvisor,
      preferences,
      openDirectory + '/' + document.name,
    );
  };

  const shouldDisableOnlyFolderActions =
    !isAdvisor && preferences?.disable_folder_action;

  const shouldDisableFileEditActions =
    !isAdvisor && preferences?.disable_file_edit_action;

  const shouldDisableClientActions: boolean =
    getIsFolderOfClientActionsFolderDisabled();

  const onPreviewFile = async () => {
    const item: DocumentItem | undefined = selectedDocuments?.find(
      (d) => d.link === document?.link,
    );

    if (item?.link && token) {
      const originalLink = item.link;

      const { document } = await downloadDocument({
        companyId: companyId!,
        link: originalLink,
        token,
      });

      // Set metadata before preview file to avoid race conditions
      if (isAdvisor && preferences?.annotation_enabled) {
        setPreviewCompanyIdForAnnotations(companyId);
        setPreviewExtractedDocumentId(item?.extracted_document_id);
        setPreviewDocumentCreatedAt(item?.created_at);
        setPreviewDocumentStatus(item?.status);
      }
      setPreviewFileOriginalLink(originalLink);

      // Set preview file last to trigger dialog with all metadata ready
      setPreviewFile(document?.data?.link);
    }
  };

  const onDeleteFile = () => onFileDelete(document);
  const onRenameFile = () => onFileRename(document);
  const onDescEdit = () => onEditDescription(document);
  const onTagsEdit = () => onEditTags(document);
  const onAttachFile = () => onAttachToQuestionnaire(document);

  const onDownloadFile = async () => {
    const isFile = document?.type === 'file';

    if (isFile) {
      const { document: d } = await downloadDocument({
        companyId: companyId!,
        link: document?.link!,
        isDownload: true,
        token,
      });

      saveAs(d?.data?.link);
    }
  };

  const onCopyFileUrl = (item: DocumentItem) => onCopyFileLocation(item);

  const onSecureShare = () => setIsSecureShareDialogOpen(true);

  const checkForExistingSecureShare = useCallback(async () => {
    if (!document || !token || !isAdvisor || isFolder || !companyId) return;

    try {
      const response = await checkExistingSecureShare({
        documentLink: document.link,
        companyId,
        token,
      });
      setHasExistingSecureShare(response.exists);
    } catch (error) {
      setHasExistingSecureShare(false); // Silently fail; just for display purposes
    }
  }, [document, token, isAdvisor, isFolder, companyId]);

  // Check for existing secure share when component mounts or document changes
  useEffect(() => {
    checkForExistingSecureShare();
  }, [checkForExistingSecureShare]);

  // Handle when a secure share is created
  const handleSecureShareCreatedOrDeleted = useCallback(() => {
    checkForExistingSecureShare();
  }, [checkForExistingSecureShare]);

  const onDownloadFolderChoice = async (shouldConvertImagesToPDF = false) => {
    const item: DocumentItem | undefined = selectedDocuments?.find(
      (d) => d.link === document?.link,
    );

    if (!item) {
      return addToast('error', 'Something went wrong.', {
        error: 'No item found in onDownloadFolderChoice',
      });
    }

    if (!companyId) {
      return addToast('error', 'Something went wrong.', {
        error: 'No companyId found in onDownloadFolderChoice',
      });
    }

    try {
      if (shouldConvertImagesToPDF) {
        await convertToPdfsAndDownloadFolder({
          companyId,
          link: item.link,
          token,
        });
      } else {
        const path = getFileItemPath(item?.link);

        await downloadFolder({
          companyId,
          link: item.link,
          name: path,
          token,
        });
      }
    } catch (error: any) {
      console.error('error', error);
      // Note from @jordan - A 503 means that the file size is too big.
      // I'm not sure if that's from AWS or Heroku or what. But that is
      // confirmed here: https://linear.app/soraban/issue/SOR-6869/[bug]-❓-unable-to-download-from-notification-dropdown
      const is503 = error?.response?.status === 503;
      const errorMessage: string = is503
        ? 'Unfortunately, that folder/file is too large to download. Can you split it up into smaller batches? Generally, the total size should be less than 150MB. And if you’re converting images to PDF, it should be less than 15MB.'
        : 'There was a problem downloading the folder. Please refresh and try again.';

      addToast('error', errorMessage, { error });
    } finally {
      setIsDownloadFolderDialogOpen(false);
    }
  };

  const hasRenameDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.edit_rename'],
    permissions,
    companyId,
  );

  const hasMoveDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.move'],
    permissions,
    companyId,
  );

  const hasCopyDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.copy'],
    permissions,
    companyId,
  );

  const hasDeleteDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.delete'],
    permissions,
    companyId,
  );

  const hasEditDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.edit'],
    permissions,
    companyId,
  );

  const hasEditDocumentTagsPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.edit_tags'],
    permissions,
    companyId,
  );

  const hasEditQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.edit'],
    permissions,
    companyId,
  );

  const getCellContent = () => {
    const actions: Array<ResultListItem> =
      isReadOnly ||
      shouldDisableClientActions ||
      (isFolder && shouldDisableOnlyFolderActions) ||
      shouldDisableFileEditActions
        ? []
        : [
            {
              name: 'Rename',
              icon: 'pencil',
              onClick: onRenameFile,
              isDisabled: !hasRenameDocumentPermission,
            },
            {
              name: 'Move',
              icon: 'arrow-right',
              onClick: () => onItemMove(document),
              isDisabled: !hasMoveDocumentPermission,
            },
            {
              name: 'Copy',
              icon: 'clipboard',
              onClick: () => onItemCopy(document),
              isDisabled: !hasCopyDocumentPermission,
            },
            {
              name: isFolder ? 'Delete Folder' : 'Delete File',
              icon: 'trash',
              onClick: onDeleteFile,
              isDisabled: !hasDeleteDocumentPermission,
            },
          ];

    const folderActions: Array<ResultListItem> = isReadOnly
      ? []
      : isAdvisor
        ? [
            {
              name: 'Download',
              icon: 'download',
              onClick: () => {
                setIsDownloadFolderDialogOpen(true);
              },
            },
            ...actions,
          ]
        : actions;

    const fileActions: Array<ResultListItem> = isDeleted
      ? [
          {
            name: 'Restore',
            onClick: () => setIsRestoreDialogOpen(true),
          },
        ]
      : isReadOnly
        ? []
        : ([
            {
              name: 'Download',
              icon: 'download',
              onClick: onDownloadFile,
            },
            ...(isAdvisor
              ? [
                  {
                    name: 'Edit Description',
                    icon: 'pencil',
                    onClick: onDescEdit,
                    isDisabled: !hasEditDocumentPermission,
                  },
                  {
                    name: 'Copy client file URL',
                    icon: 'clipboard',
                    onClick: () => onCopyFileUrl(document),
                  },
                  {
                    name: hasExistingSecureShare
                      ? 'View secure send link'
                      : 'Generate secure send link',
                    icon: 'share',
                    onClick: onSecureShare,
                  },
                  ...(preferences.document_tags_enabled
                    ? [
                        {
                          name: 'Edit Tags',
                          icon: 'tag',
                          onClick: onTagsEdit,
                          isDisabled: !hasEditDocumentTagsPermission,
                        },
                      ]
                    : []),
                  ...(!isAttached
                    ? [
                        {
                          name: 'Attach to Questionnaire',
                          icon: 'paperclip',
                          onClick: onAttachFile,
                          isDisabled: !hasEditQuestionnairePermission,
                        },
                      ]
                    : []),
                ]
              : []),
            ...actions,
          ] as Array<ResultListItem>);

    switch (type) {
      case 'select': {
        return (
          <CellCheckbox>
            <Checkbox
              label="Select"
              isLabelHidden={true}
              isChecked={isSelected}
              onChange={() => toggleSelect(document.name)}
            />
          </CellCheckbox>
        );
      }
      case 'name': {
        return (
          <CellContainer>
            <Flex
              onClick={
                isFolder
                  ? onClickFolder
                  : type === 'name'
                    ? onPreviewFile
                    : undefined
              }
              isColumn={true}
            >
              <Flex gap={2} isYCentered={true}>
                <Icon size={20} name={icon} />

                {isDeleted ? (
                  <Pill
                    intent="danger"
                    label="Deleted"
                    isDark={true}
                    size="small"
                  />
                ) : (
                  <>
                    {isNew && (
                      <Pill intent="secondary" size="small" label="New" />
                    )}
                  </>
                )}

                <Text type="tiny">{document.name}</Text>
              </Flex>
              {isAttached && (
                <Text type="tiny" color="disabled">
                  {attachedQuestionnaire}
                </Text>
              )}
            </Flex>
          </CellContainer>
        );
      }
      case 'description': {
        return isAdvisor && <CellContainer text={document.description} />;
      }
      case 'tags': {
        const hasTag: boolean = isAdvisor && !!document?.tags?.length;

        if (!hasTag || !isAdvisor || !preferences.document_tags_enabled) {
          return null;
        }

        return (
          <CellContainer>
            <Flex gap={1} isWrapping={true} padding={2}>
              {document?.tags?.map((tag, key) => {
                return <Pill key={key} intent="secondary" label={tag} />;
              })}
            </Flex>
          </CellContainer>
        );
      }
      case 'updated_at': {
        return (
          <CellContainer>
            <Text type="tiny">
              <Timestamp timestamp={document.updated_at} />
            </Text>
          </CellContainer>
        );
      }
      case 'created_at': {
        return (
          <CellContainer>
            <Text type="tiny">
              <Timestamp timestamp={document.created_at} />
            </Text>
          </CellContainer>
        );
      }
      case 'actions': {
        return (
          <CellContainer isActions={true}>
            <Dropdown
              items={isFolder ? folderActions : fileActions}
              isDisabled={isPreview}
              placement="left"
            >
              <Button
                value="Actions"
                isIconOnly={true}
                intent="ghost"
                isDisabled={
                  isPreview ||
                  shouldDisableClientActions ||
                  (isFolder && shouldDisableOnlyFolderActions)
                }
                iconLeft="dots-three"
                size="small"
                tooltip="Disabled by firm"
                withTooltip={
                  shouldDisableClientActions ||
                  (isFolder && shouldDisableOnlyFolderActions)
                }
              />
            </Dropdown>
          </CellContainer>
        );
      }
      default:
        return null;
    }
  };

  const documentsCellClasses: string = getClasses(className, {
    'cursor-pointer': type === 'name',
    sorabanTable__fixedColumn: isLastColumn,
  });

  const cellContent: ReactNode = getCellContent();

  return (
    <>
      <Dialog
        isOpen={isDownloadFolderDialogOpen}
        onClose={() => setIsDownloadFolderDialogOpen(false)}
        title="Convert and download"
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            <Text>
              Do you want to convert all images to PDFs and download? This
              process might take a few minutes. You will receive a notification
              when the zip file is ready for download.
            </Text>
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="right">
            <Button
              intent="secondary"
              onClick={() => onDownloadFolderChoice(false)}
              value="No. Don't convert images to PDFs."
            />
            <Button
              intent="primary"
              onClick={() => onDownloadFolderChoice(true)}
              value="Yes. Convert images to PDFs."
            />
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>

      <RestoreDocumentDialog
        isOpen={isRestoreDialogOpen}
        onClose={() => setIsRestoreDialogOpen(false)}
        selectedDocuments={[document]}
      />
      <SecureDocumentShareDialog
        isOpen={isSecureShareDialogOpen}
        onClose={() => setIsSecureShareDialogOpen(false)}
        document={document}
        companyId={companyId!}
        onSecureShareModified={handleSecureShareCreatedOrDeleted}
      />
      <td className={documentsCellClasses}>{cellContent}</td>
    </>
  );
};
